import type { StyleDefinition, StyleFilterExpression, StyleLayer } from '../types.ts';

/**
 * Identifies and hides venue style layers that visually conflict with
 * map surround (positron) layers.
 *
 * Conflicting categories:
 *  - basemap.transitline → duplicated by positron-railway-*
 *  - basemap.road        → duplicated by positron-highway-*
 *  - basemap.outdoor     → duplicated by positron-water, positron-park
 *  - basemap.runway      → duplicated by positron-aeroway-* (surround owns aviation surfaces)
 *  - basemap.runwayhole  → duplicated by positron-aeroway-* (surround owns aviation surfaces)
 *
 * Only layers whose category filter **exclusively** matches one of these
 * categories are hidden.  Multi-category layers (e.g. "polygons") are left
 * untouched because they render many non-conflicting features too.
 */

const CONFLICTING_CATEGORIES = new Set([
  'basemap.transitline',
  'basemap.road',
  'basemap.outdoor',
  'basemap.runway',
  'basemap.runwayhole',
]);

/**
 * Extracts the matched category values from a Mapbox/MapLibre filter expression.
 *
 * Supported structures:
 *  - ["match", ["get", "category"], ["cat1", "cat2"], true, false]
 *  - ["match", ["get", "category"], "singleCat", true, false]
 *  - ["all", ..., <match expr>, ...]  (nested inside "all")
 *
 * @param {Array} filter - A Mapbox GL style filter expression
 * @returns {string[]} The category values from the first `match` on `category`, or []
 */
export const extractCategories = (filter: unknown): string[] => {
  if (!Array.isArray(filter) || filter.length === 0) {
    return [];
  }

  const [op, ...rest] = filter as StyleFilterExpression;

  if (op === 'match') {
    // ["match", ["get", "category"], <values>, true, false]
    const [accessor, values] = rest;
    if (Array.isArray(accessor) && accessor[0] === 'get' && accessor[1] === 'category') {
      return Array.isArray(values) ? (values as string[]) : typeof values === 'string' ? [values] : [];
    }
    return [];
  }

  if (op === 'all') {
    // Recurse into each sub-expression, return first match found
    for (const sub of rest) {
      const cats = extractCategories(sub);
      if (cats.length > 0) {
        return cats;
      }
    }
  }

  return [];
};

/**
 * Returns true when every category in the layer's filter is a conflicting one.
 * Layers with mixed categories (e.g. "polygons") are NOT conflicting.
 *
 * @param {object} layer - A Mapbox GL style layer object
 * @returns {boolean}
 */
export const isConflictingLayer = (layer: StyleLayer): boolean => {
  const categories = extractCategories(layer.filter);
  return categories.length > 0 && categories.every(cat => CONFLICTING_CATEGORIES.has(cat));
};

/**
 * Mutates a Mapbox GL style object in-place, setting `layout.visibility = 'none'`
 * on every layer that exclusively renders conflicting basemap categories.
 *
 * Returns the IDs of the hidden layers so they can be restored later.
 *
 * @param {{ layers: object[] }} style - A Mapbox GL style object (mutated in-place)
 * @returns {string[]} IDs of layers that were hidden
 */
export const hideConflictingLayers = (style: StyleDefinition): string[] => {
  const hidden: string[] = [];
  for (const layer of style.layers) {
    if (isConflictingLayer(layer)) {
      layer.layout = { ...layer.layout, visibility: 'none' };
      hidden.push(layer.id);
    }
  }
  return hidden;
};
